type HighlightRange = { start: number; end: number };

const hasMarkdownEmphasis = (value: string): boolean => /\*\*|_/.test(value);

const addRange = (text: string, ranges: HighlightRange[], start: number, end: number) => {
  let rangeStart = start;
  let rangeEnd = end;

  while (rangeStart < rangeEnd && /\s/.test(text[rangeStart])) {
    rangeStart += 1;
  }

  while (rangeEnd > rangeStart && /\s/.test(text[rangeEnd - 1])) {
    rangeEnd -= 1;
  }

  if (rangeEnd - rangeStart < 3) {
    return;
  }

  const overlaps = ranges.some((range) => {
    const maxStart = Math.max(range.start, rangeStart);
    const minEnd = Math.min(range.end, rangeEnd);
    return maxStart < minEnd;
  });

  if (overlaps) {
    return;
  }

  ranges.push({ start: rangeStart, end: rangeEnd });
};

const applyRanges = (text: string, ranges: HighlightRange[]): string => {
  if (ranges.length === 0) {
    return text;
  }

  const sorted = [...ranges].sort((a, b) => b.start - a.start);
  let result = text;

  sorted.forEach(({ start, end }) => {
    result = `${result.slice(0, start)}**${result.slice(start, end)}**${result.slice(end)}`;
  });

  return result;
};

const highlightQuotes = (text: string, ranges: HighlightRange[]) => {
  const quoteRegex = /"([^"\n]{3,})"/g;
  let match: RegExpExecArray | null;

  while ((match = quoteRegex.exec(text)) !== null) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [_fullMatch, inner] = match;
    if (!inner) {
      continue;
    }
    const start = match.index + 1; // skip opening quote
    const end = start + inner.length;
    addRange(text, ranges, start, end);
  }
};

const highlightProperNouns = (text: string, ranges: HighlightRange[], maxHighlights: number) => {
  const properNounRegex = /\b([A-Z][\p{L}]+(?:\s+(?:[A-Z][\p{L}]+|[A-Z]\.|de|di|da|del|des|la|le|el|los|las|of|the|San|Santa|Santo|St\.|Mount|Fort|New|Old|Lake|Rio|Mission|Port|Cape|Las|Los)[\p{L}.]*){1,4})\b/gu;
  let match: RegExpExecArray | null;

  while ((match = properNounRegex.exec(text)) !== null) {
    if (ranges.length >= maxHighlights) {
      break;
    }

    const matchedText = match[1];
    if (!matchedText || matchedText.length < 4) {
      continue;
    }

    const start = match.index;
    const end = start + matchedText.length;
    addRange(text, ranges, start, end);
  }
};

const highlightNumbers = (text: string, ranges: HighlightRange[], maxHighlights: number) => {
  const numberRegex = /\b\d[\d,]*(?:\.\d+)?(?:\s+(?:percent|%|miles?|kilometers?|km|meters?|metres?|feet|ft|acres|breweries|beaches|museums|attractions|galleries|parks|years?|centuries|people|residents|islands|brews|crafts|degrees|°F|°C))?/gi;
  let match: RegExpExecArray | null;

  while ((match = numberRegex.exec(text)) !== null) {
    if (ranges.length >= maxHighlights) {
      break;
    }

    const matchedText = match[0];
    const start = match.index;
    const end = start + matchedText.length;
    addRange(text, ranges, start, end);
  }
};

const highlightSuperlatives = (text: string, ranges: HighlightRange[], maxHighlights: number) => {
  const keywords = ['first', 'largest', 'oldest', 'only', 'most', 'best', 'longest', 'highest', 'deepest', 'tallest', 'biggest', 'renowned', 'famous', 'iconic', 'notable'];
  const pattern = `\\b(${keywords.join('|')})\\s+[^\\s,.!?;:]+(?:\\s+[^\\s,.!?;:]+){0,3}`;
  const superlativeRegex = new RegExp(pattern, 'gi');
  let match: RegExpExecArray | null;

  while ((match = superlativeRegex.exec(text)) !== null) {
    if (ranges.length >= maxHighlights) {
      break;
    }

    const matchedText = match[0];
    const start = match.index;
    const end = start + matchedText.length;
    addRange(text, ranges, start, end);
  }
};

const highlightFallbackWords = (text: string, ranges: HighlightRange[]) => {
  const wordRegex = /\b[\p{L}\p{N}][\p{L}\p{N}’’-]*\b/gu;
  const words = Array.from(text.matchAll(wordRegex));

  if (words.length === 0) {
    return;
  }

  const highlightWordSpan = (startWordIndex: number, count: number) => {
    const startMatch = words[startWordIndex];
    const endMatch = words[Math.min(startWordIndex + count - 1, words.length - 1)];

    if (!startMatch || !endMatch) {
      return;
    }

    const start = startMatch.index ?? 0;
    const end = (endMatch.index ?? 0) + endMatch[0].length;
    addRange(text, ranges, start, end);
  };

  const attempts: Array<[number, number]> = [];

  if (words.length > 0) {
    attempts.push([0, Math.min(3, words.length)]);
  }

  if (words.length > 4) {
    attempts.push([Math.max(1, Math.floor(words.length / 2) - 1), Math.min(3, words.length)]);
  }

  if (words.length > 2) {
    attempts.push([Math.max(words.length - 3, 0), Math.min(3, words.length)]);
  }

  for (const [start, count] of attempts) {
    if (ranges.length >= 2) {
      break;
    }
    const before = ranges.length;
    highlightWordSpan(start, count);
    if (before === ranges.length) {
      continue;
    }
  }

  if (ranges.length < 2) {
    for (let i = 0; i < words.length && ranges.length < 2; i += 1) {
      const before = ranges.length;
      highlightWordSpan(i, 1);
      if (ranges.length > before) {
        continue;
      }
    }
  }
};

const highlightParagraph = (paragraph: string, maxHighlights: number): string => {
  if (!paragraph.trim()) {
    return paragraph;
  }

  const ranges: HighlightRange[] = [];

  highlightQuotes(paragraph, ranges);
  highlightProperNouns(paragraph, ranges, maxHighlights);
  highlightSuperlatives(paragraph, ranges, maxHighlights);
  highlightNumbers(paragraph, ranges, maxHighlights);

  if (ranges.length < 2) {
    highlightFallbackWords(paragraph, ranges);
  }

  const limitedRanges = ranges.slice(0, maxHighlights);

  return applyRanges(paragraph, limitedRanges);
};

export const emphasizeMarkdownLead = (input?: string | null, options?: { maxHighlights?: number }): string => {
  if (!input) {
    return '';
  }

  if (hasMarkdownEmphasis(input)) {
    return input;
  }

  const maxHighlights = options?.maxHighlights ?? 6;
  const paragraphs = input.split(/\n{2,}/);

  if (paragraphs.length === 0) {
    return input;
  }

  const highlightedParagraphs = paragraphs.map((paragraph) => highlightParagraph(paragraph, maxHighlights));

  return highlightedParagraphs.join('\n\n');
};
